import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, ArrowRight, Trophy, Star, Sparkles, TrendingUp, MessageCircle, Users } from 'lucide-react';
import { cjmsApi } from '../api/cjmsApi';
import { feedbackBus } from '../services/feedbackBus';

interface MilestoneTrackerProps {
    leadId?: string;
    email: string;
}

const MILESTONES = [
    { id: '0', label: 'Just Started', icon: Clock, description: 'First 5 customers' },
    { id: '5', label: 'Halfway There', icon: TrendingUp, description: '5 customers acquired — unlock insights' },
    { id: '10', label: 'First Major Milestone', icon: Trophy, description: '10 customers — upgrade to partner tier', upgrade: 'Goal-to-100 Partner (R6,997/mo)' },
    { id: '50', label: 'Scale Up', icon: Star, description: '50 customers — maximum unlock potential' },
    { id: '100', label: 'Major Milestone', icon: Trophy, description: '100 customers — enterprise ready' },
    { id: '1000', label: 'Raising the Bar', icon: Sparkles, description: '1000 customers — leadership tier' },
];

export const MilestoneTracker: React.FC<MilestoneTrackerProps> = ({
    leadId = '',
    email = '',
}) => {
    const [customerCount, setCustomerCount] = useState<number>(0);
    const [milestone10, setMilestone10] = useState<boolean>(false);
    const [percentTo10, setPercentTo10] = useState<number>(0);
    const [milestones, setMilestones] = useState<any[]>([]);
    const [completedMilestones, setCompletedMilestones] = useState<string[]>([]);
    const [step, setStep] = useState<'loading' | 'success' | 'error'>('loading');

    useEffect(() => {
        const fetchCount = async () => {
            try {
                const result = await cjmsApi.getCustomerCount(email);
                if (result.success) {
                    setCustomerCount(result.customerCount || 0);
                    setMilestone10(result.milestone10 || false);
                    setPercentTo10(result.percentTo10 || 0);

                    // Determine which milestones are completed
                    const completed: string[] = [];
                    for (const m of MILESTONES) {
                        const milestoneValue = parseInt(m.id);
                        if (milestoneValue > 0 && customerCount >= milestoneValue) {
                            completed.push(m.id);
                        }
                    }
                    setCompletedMilestones(completed);
                    setMilestones(result.milestones || []);
                    setStep('success');
                } else {
                    setStep('error');
                }
            } catch {
                setStep('error');
            }
        };

        fetchCount();
    }, [email]);

    const handleCheck = async () => {
        setStep('loading');
        try {
            const result = await cjmsApi.checkMilestones({ email });
            if (result.success) {
                setCustomerCount(result.customerCount || 0);
                setMilestone10(result.milestone10 || false);
                setPercentTo10(Math.min(Math.round((result.customerCount / 10) * 100), 100));
                setMilestones(result.milestones || []);

                const completed: string[] = [];
                for (const m of MILESTONES) {
                    const milestoneValue = parseInt(m.id);
                    if (milestoneValue > 0 && result.customerCount >= milestoneValue) {
                        completed.push(m.id);
                    }
                }
                setCompletedMilestones(completed);

                // If milestone triggered
                if (result.milestones?.length > 0) {
                    feedbackBus.emit('milestone_reached', {
                        milestones: result.milestones,
                        customerCount: result.customerCount,
                    });
                }

                setStep('success');
            } else {
                setStep('error');
            }
        } catch {
            setStep('error');
        }
    };

    if (step === 'loading') {
        return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-lg mx-auto p-6 text-center">
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                    <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
                    <h2 className="text-lg font-bold text-slate-800 mb-2">Checking Your Progress</h2>
                    <p className="text-xs text-slate-500">Let's see how many customers you've acquired...</p>
                </div>
            </motion.div>
        );
    }

    if (step === 'error') {
        return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-lg mx-auto p-6 text-center">
                <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                    <p className="text-sm text-red-600 font-medium mb-4">Unable to load milestone data</p>
                    <button onClick={handleCheck} className="px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                        Retry
                    </button>
                </div>
            </motion.div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-amber-600" />
                    <h3 className="text-sm font-bold text-slate-800">Milestone Tracker</h3>
                </div>
                <span className="text-[10px] font-medium text-amber-600">{customerCount} customers</span>
            </div>

            <div className="px-4 pt-4 pb-2">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] text-slate-500 font-medium">To 10 Customers</span>
                    <span className="text-[10px] text-amber-600 font-bold">{percentTo10}%</span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${percentTo10}%` }}
                        className="h-full bg-gradient-to-r from-amber-500 to-amber-600 rounded-full"
                    />
                </div>
            </div>

            <div className="p-4 space-y-3">
                {MILESTONES.map((milestone, idx) => {
                    const Icon = milestone.icon;
                    const isCompleted = completedMilestones.includes(milestone.id);
                    const isNext = parseInt(milestone.id) > 0 && customerCount >= parseInt(milestone.id) - 5;
                    const isCurrent = parseInt(milestone.id) === Math.ceil(percentTo10);

                    return (
                        <div
                            key={milestone.id}
                            className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                                isCompleted
                                    ? 'bg-green-50 border-green-200'
                                    : isCurrent
                                        ? 'bg-amber-50 border-amber-300'
                                        : isNext
                                            ? 'bg-blue-50 border-blue-100'
                                            : 'bg-slate-50 border-slate-100'
                            }`}
                        >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                isCompleted ? 'bg-green-100' :
                                isCurrent ? 'bg-amber-100' :
                                isNext ? 'bg-blue-100' :
                                'bg-slate-100'
                            }`}>
                                {isCompleted ? (
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                ) : isCurrent ? (
                                    <Sparkles className="w-4 h-4 text-amber-600" />
                                ) : isNext ? (
                                    <Users className="w-4 h-4 text-blue-600" />
                                ) : (
                                    <Clock className="w-4 h-4 text-slate-400" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <p className={`text-xs font-semibold ${
                                        isCompleted ? 'text-green-700' :
                                        isCurrent ? 'text-amber-700' :
                                        isNext ? 'text-blue-700' :
                                        'text-slate-500'
                                    }`}>{milestone.label}</p>
                                    {milestone.id === '10' && milestone10 && (
                                        <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">
                                            Reach!
                                        </span>
                                    )}
                                </div>
                                <p className="text-[10px] text-slate-500">{milestone.description}</p>
                                {milestone.upgrade && (
                                    <p className="text-[10px] text-amber-600 font-medium mt-1">
                                        Upgrade: {milestone.upgrade}
                                    </p>
                                )}
                            </div>
                            {isCompleted && (
                                <CheckCircle className="w-3 h-3 text-green-500" />
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Milestone rewards */}
            {milestones.length > 0 && (
                <div className="px-4 pb-4">
                    <div className="p-3 bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg border border-amber-200">
                        <div className="flex items-center gap-2 mb-2">
                            <Trophy className="w-4 h-4 text-amber-600" />
                            <p className="text-xs font-bold text-amber-800">Milestone Unlocked!</p>
                        </div>
                        <ul className="space-y-1">
                            {milestones.map((m, i) => (
                                <li key={i} className="text-[10px] text-amber-700">
                                    <CheckCircle className="w-3 h-3 inline mr-1" />
                                    {m.label}: {m.description}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {/* Support */}
            <div className="px-4 pb-4">
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100">
                    <MessageCircle className="w-3 h-3 text-slate-400" />
                    <p className="text-[9px] text-slate-600">
                        Questions about milestones? WhatsApp +27 81 714 2228 — responded within 6-12 hours.
                    </p>
                </div>
            </div>
        </motion.div>
    );
};
